import React, { useMemo, useState } from 'react';

const STANDARD_ACTIONS = ['CREATE', 'READ', 'SHOW', 'UPDATE', 'DELETE', 'APPROVE'];

const PermissionMatrix = ({ permissions }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');

  // Parse and group permissions
  const groupedData = useMemo(() => {
    const groups = {};
    const allActions = new Set(STANDARD_ACTIONS);

    permissions.forEach((perm) => {
      const code = perm.code || '';
      const parts = code.split(':');
      
      let module = perm.module || parts[0] || 'SYSTEM';
      let resource = parts[1] || parts[0] || 'GENERAL';
      let action = parts[2] || 'READ';
      
      module = module.trim().toUpperCase();
      resource = resource.trim().toUpperCase();
      action = action.trim().toUpperCase();

      allActions.add(action);

      const key = `${module}:${resource}`;
      if (!groups[key]) {
        groups[key] = {
          module,
          resource,
          names: new Set(),
          actions: {},
          descriptions: new Set(),
        };
      }
      groups[key].names.add(perm.name);
      if (perm.description) {
        groups[key].descriptions.add(perm.description);
      }
      groups[key].actions[action] = true;
    });

    // Sort actions
    const sortedActions = Array.from(allActions).sort((a, b) => {
      const idxA = STANDARD_ACTIONS.indexOf(a);
      const idxB = STANDARD_ACTIONS.indexOf(b);
      const valA = idxA === -1 ? 999 : idxA;
      const valB = idxB === -1 ? 999 : idxB;
      if (valA !== valB) return valA - valB;
      return a.localeCompare(b);
    });

    const list = Object.values(groups).map(g => ({
      ...g,
      permissionNames: Array.from(g.names).join(', '),
      description: Array.from(g.descriptions).join(' | '),
    }));

    // Sort rows by Module then Resource
    list.sort((a, b) => {
      if (a.module !== b.module) return a.module.localeCompare(b.module);
      return a.resource.localeCompare(b.resource);
    });

    return { list, sortedActions };
  }, [permissions]);

  // Filter list
  const filteredList = useMemo(() => {
    return groupedData.list.filter(item => {
      const matchesSearch = !searchTerm ||
        item.resource.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.permissionNames.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesModule = !moduleFilter || item.module === moduleFilter;

      return matchesSearch && matchesModule;
    });
  }, [groupedData.list, searchTerm, moduleFilter]);

  const uniqueModules = useMemo(() => {
    return [...new Set(groupedData.list.map(item => item.module))].sort();
  }, [groupedData.list]);

  return (
    <div className="space-y-4">
      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search resources or permissions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 bg-gray-50 text-gray-900"
          />
        </div>
        <div className="sm:w-44">
          <select
            value={moduleFilter}
            onChange={(e) => setModuleFilter(e.target.value)}
            className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 bg-gray-50 text-gray-900"
          >
            <option value="">All Modules</option>
            {uniqueModules.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table Container */}
      <div className="overflow-x-auto border border-gray-200 rounded-lg shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Module
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Resource
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Permission Name
              </th>
              {groupedData.sortedActions.map(action => (
                <th
                  key={action}
                  className="px-2 py-2 text-center text-[10px] font-semibold text-gray-700 uppercase tracking-wider"
                  title={`Action: ${action}`}
                >
                  {action}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredList.length === 0 ? (
              <tr>
                <td
                  colSpan={3 + groupedData.sortedActions.length}
                  className="px-4 py-8 text-center text-xs text-gray-500"
                >
                  No matching permissions found.
                </td>
              </tr>
            ) : (
              filteredList.map((item) => (
                <tr key={`${item.module}:${item.resource}`} className="hover:bg-gray-50 transition-colors">
                  <td className="px-3 py-2 whitespace-nowrap text-xs font-bold text-indigo-600">
                    {item.module}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs font-semibold text-gray-800">
                    {item.resource}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600" title={item.description}>
                    {item.permissionNames}
                  </td>
                  {groupedData.sortedActions.map(action => {
                    const hasAction = item.actions[action];
                    return (
                      <td key={action} className="px-2 py-2 whitespace-nowrap text-center text-xs">
                        {hasAction ? (
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-50 text-green-600 font-bold border border-green-200" title={`${item.module}:${item.resource}:${action} - Active`}>
                            Y
                          </span>
                        ) : (
                          <span className="text-gray-300 font-medium">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PermissionMatrix;
